import cloneDeep from 'lodash/cloneDeep'
import { toGeoJSON } from '@mapbox/polyline'
import throttle from 'lodash/throttle'
import * as turf from '@turf/turf'
import { reactive, readonly, watch } from 'vue'

import {
  httpGetJson,
  httpPostJson,
  httpPutJson,
  httpDeleteJson,
} from '@/lib/net'
import Geographic from '@/lib/geographic'
import { camera } from '@/services/map-service'
import pubsub from '@/lib/pubsub'
import {
  BUILD_MODE,
  DEFAULT_SNAP_RADIUS,
  DEFAULT_PREVIEW_THROTTLE_INTERVAL,
  MAX_PREVIEW_DISTANCE_KM,
} from '@/services/route-service-constants'

/**
 * The data representation of a route from the routes API.
 * @typedef {Object} RouteData
 * @property {String} id
 * @property {Object} route - The route payload received from the directions API.
 * @property {String} name
 * @property {String} notes
 * @property {String} color
 * @property {String} style
 * @property {String} weight
 * @property {Date} createdAt - Returned with server responses
 * @property {Date} lastModifiedAt - Returned with server responses
 * @property {Object} geoJson - Memoized with server responses
 * @returns
 */

/**
 * Save a route in persistent storage.
 * @param {RouteMarkup} route
 * @returns {{data: RawRoute}}
 */
export const createRoute = async function (route) {
  return await httpPostJson('/v1/routing/routes', {
    data: route,
  })
}

/**
 * Returns a page of saved routes.
 * @param {Object} options
 * @param {Number} options.pageSize
 * @param {Number} options.pageAfter
 * @returns {{data: Array<RouteData>, page: Object}}
 */
export const listRoutes = async function ({
  pageSize = 50,
  pageAfter = null,
} = {}) {
  // Exclude steps (turn-by-turn instructions) to minimize payload.
  const params = ['excludeSteps', `page[size]=${pageSize}`]
  if (pageAfter) {
    params.push(`page[after]=${pageAfter}`)
  }

  const query = params.join('&')
  return await httpGetJson(`/v1/routing/routes?${query}`)
}

/**
 * Returns all pages of saved routes.
 * @returns {{data: Array<RawRoute>}}
 */
export const listAllRoutes = async function () {
  const res = []

  // First page.
  let routes = await listRoutes()
  res.push(...routes.data)

  // Additional pages.
  while (routes.page.after) {
    routes = await listRoutes({ pageAfter: routes.page.after })
    res.push(...routes.data)
  }

  return { data: res }
}

/** Returns a saved route by id.
 * @param {String} routeId - A ULID identifier for the route.
 * @returns {{data: RouteData}}
 */
export const getRoute = async function (routeId) {
  return await httpGetJson(`/v1/routing/routes/${routeId}`)
}

/**
 * Update a saved route.
 * @param {String} routeId - A ULID identifier for the route.
 * @param {RouteMarkup} route
 * @returns {{data: RawRoute}}
 */
export const updateRoute = async function (routeId, route) {
  return await httpPutJson(`/v1/routing/routes/${routeId}`, route)
}

/**
 * Delete a saved route.
 * @param {String} routeId - A ULID identifier for the route.
 */
export const deleteRoute = async function (routeId) {
  await httpDeleteJson(`/v1/routing/routes/${routeId}`)
}

/**
 * Delete a collection of routes.
 * @param {Array<String>} routeIds - ULID identifiers for the routes.
 */
export const deleteRoutes = async function (routeIds) {
  await httpPostJson(`/v1/routing/routes/delete`, { data: routeIds })
}

/**
 * Log waypoints used in a discarded route to preserve possible analytics.
 * @param {Route} route
 */
export const logWaypoints = async function (route) {
  await httpPostJson(`/v1/routing/routes/waypoints`, {
    waypoints: route.waypoints,
    tilesetName: route.data.tilesetName,
    tilesetVersion: route.data.tilesetVersion,
  })
}

/**
 * Requests a route from the routing engine.
 * @param {Array<Object>} waypoints Waypoint objects used in the route request.
 * @param {Object} options
 * @param {Boolean} options.excludeSteps Whether to exclude turn-by-turn directions in the response.
 * @param {Object} options.http Options passed to the underlying HTTP fetch invocation.
 * @returns {Route} The best route found, if any.
 */
export const fetchRoute = async function (waypoints, options = {}) {
  if (waypoints.length < 2) {
    return
  }

  // Build the request body.
  const requestBody = {
    options: {
      excludeSteps: !!options.excludeSteps,
    },
    routes: [
      {
        legs: [{ waypoints }],
        options: {
          freeUnsnappedWaypoints: true,
          constrainToCluster: true,
        },
      },
    ],
  }

  // Use non-motorized trail set AND motorized trail set (/backcountry)
  const path = '/v1/backcountry/routing/directions'

  try {
    const data = await httpPostJson(path, requestBody, options.http)
    return new Route(data.routes[0])
  } catch (err) {
    // Custom handling requires a response/status to test.
    if (!err.response?.status) {
      throw err
    }

    const { status } = err.response
    const data = await err.response?.json()
    if (status === 404) {
      // Indistinct waypoints will naturally occur sometimes when snapping
      // distinct waypoints to a dead-end (both will snap to the same position).
      // Ignore these errors.
      if (data?.reason === 'indistinct_waypoints') {
        return
      }
      throw 'No route was found. Please try again with different points.'
    } else {
      throw 'An error occurred while fetching the route. Please try again later or contact support.'
    }
  }
}

/**
 * SerialRouteFetcher manages fetch requests for routes, ensuring that requests
 * are resolved in the order they were requested.
 */
export class SerialRouteFetcher {
  constructor() {
    // A queue of requests to be resolved in order.
    this._requestQueue = []
    this._requestCounter = 0
  }

  /**
   * Fetch a route, ensuring that stale fetch requests are aborted.
   * @param {Array<Object>} waypoints
   * @returns {{route: Route, aborted: Boolean}} Returns the route if the fetch
   * response is most recent; otherwise, if the request is stale and should be
   * discarded, the `route` will be null and `aborted` will be true.
   */
  async fetch(waypoints) {
    // Track the request and an abort controller to cancel stale requests after
    // resolving more recent ones.
    const requestId = this._requestCounter++
    const controller = new AbortController()
    this._requestQueue.push({ requestId, controller })

    // Fetch the route, passing the abort signal to the underlying fetch call.
    let route = null
    try {
      route = await fetchRoute(waypoints, {
        excludeSteps: true,
        http: { signal: controller.signal },
      })
    } catch (err) {
      // Bail completely if the requst was aborted.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { route, aborted: true }
      }
      throw err
    }

    // After resolving the request (async), find the current index of the request.
    // This can change when other requests resolve and clear items from the queue.
    const requestIndex = this._requestQueue.findIndex(
      (req) => req.requestId === requestId
    )

    // Abort all requests previous to this that are pending resolution.
    for (let i = 0; i < requestIndex; i++) {
      this._requestQueue[i].controller.abort()
    }

    // Remove aborted and resolved requests from the queue.
    this._requestQueue.splice(0, requestIndex + 1)

    return { route, aborted: false }
  }

  /**
   * Abort all pending requests.
   */
  abortAll() {
    // Abort all requests that are pending resolution.
    for (const request of this._requestQueue) {
      request.controller.abort()
    }
    this._requestQueue = []
  }
}

// manage button clicks for route-builder and pass to analytics.
export const eventsStore = reactive({
  pointDrawClickedCount: 0,
  snapToClickedCount: 0,
  errors: 0,
  undoClickedCount: 0,
  clearClickedCount: 0,
  helpClickedCount: 0,
  collapsedCount: 0,
  snapToToggle(shouldSnap) {
    if (shouldSnap) {
      this.snapToClickedCount++
    } else {
      this.pointDrawClickedCount++
    }
  },
  updateErrorCount() {
    this.errors++
  },
  updateUndoCount() {
    this.undoClickedCount++
  },
  updateClearCount() {
    this.clearClickedCount++
  },

  updateHelpCount() {
    this.helpClickedCount++
  },

  updateCollapsedCount() {
    this.collapsedCount++
  },

  resetEventsStore() {
    this.pointDrawClickedCount = 0
    this.snapToClickedCount = 0
    this.errors = 0
    this.undoClickedCount = 0
    this.clearClickedCount = 0
    this.helpClickedCount = 0
    this.collapsedCount = 0
  },
})

// The cleared state representing no current route.
const clearState = Object.freeze({
  // The locations the current route travels through.
  waypoints: [],

  // Waypoints pending a sync against the server.
  syncWaypoints: [],

  // The current route managed by the service.
  route: null,

  // The GeoJSON for a route preview, if one exists.
  previewSegment: null,

  // The error received from the last request for a route or preview.
  error: null,
})

// The zeroed state for the route service.
const resetState = Object.freeze({
  ...clearState,

  // An initial route to use to seed the build state.
  initialRoute: null,

  // Determines whether to snap new waypoints to the routing graph.
  shouldSnap: true,

  // Saved version of shouldSnap while editing a waypoint
  shouldSnapTemp: null,

  // Stack used to manage undo/redo state changes.
  undoStack: [{ route: null, waypoints: [] }],
  // The current index of the stack.
  undoStackIndex: 0,
  // Whether an undo operation is valid.
  canUndo: false,
  // Whether a redo operation is valid.
  canRedo: false,
  // Index of an actively focused route waypoint
  focusedWaypointIndex: null,
})

/**
 * RouteService manages the route building experience for a single route.
 */
class RouteService {
  /**
   * Initialization function.
   */
  constructor() {
    this._previewFetcher = new SerialRouteFetcher()

    // Observable state.
    const state = reactive({
      ...cloneDeep(resetState),

      // One of `build` or `edit`. In `build` mode, previews are rendered to
      // follow the user's current mouse position before they add a waypoint.
      // To prevent collisions of intent between "add a waypoint" vs "update a
      // waypoint", users can only add waypoints in `build` mode. In `edit` mode,
      // previews are disabled and the waypoints can be updated.
      mode: BUILD_MODE,
    })
    this._state = state

    // Watch when the initial route is set.
    watch(
      () => state.initialRoute,
      (initialRoute) => {
        this.reset()
        state.route = initialRoute
        state.undoStack[0].route = initialRoute
      },
      { flush: 'sync' }
    )

    // Sync state immediately on changes to the route.
    watch(
      () => this._state.route,
      (route) => {
        this._state.waypoints = route?.waypointLocations || []
        this._resetPreview()
      },
      { flush: 'sync' }
    )

    // Bound/throttled handlers.
    this._syncPreviewThrottled = throttle(
      this._syncPreview.bind(this),
      DEFAULT_PREVIEW_THROTTLE_INTERVAL
    )
  }

  /**
   * Returns the current interaction mode.
   * @returns {String} One of BUILD_MODE or EDIT_MODE.
   */
  get mode() {
    return readonly(this._state).mode
  }

  /**
   * Sets the current interaction mode.
   * @param {String} value One of BUILD_MODE or EDIT_MODE.
   */
  set mode(value) {
    this._state.mode = value
    this._resetPreview()
  }

  /**
   * Sets whether to snap new waypoints added to the route.
   * @param {Boolean} value
   */
  set shouldSnap(value) {
    this._state.shouldSnap = value
    eventsStore.snapToToggle(value)
  }

  /**
   * Returns true for snap to, false for point draw.
   * @param {Boolean}
   */
  get shouldSnap() {
    return readonly(this._state).shouldSnap
  }

  /**
   * Returns the current waypoints.
   * @returns {Array<LngLat>} A reactive array of [lng, lat].
   */
  get waypoints() {
    return readonly(this._state).waypoints
  }

  /**
   * Returns the current route.
   * @returns {Object} A reactive route.
   */
  get route() {
    return readonly(this._state).route
  }

  /**
   * Sets the initial route when editing.
   * @param {Route} route
   */
  set initialRoute(route) {
    this._state.initialRoute = route
  }

  /**
   * Returns the current preview.
   * @returns {GeoJSON} A reactive preview.
   */
  get previewSegment() {
    return readonly(this._state).previewSegment
  }

  /**
   * Returns the last error encountered while fetching a route or preview.
   * @returns {Object} A reactive error.
   */
  get error() {
    return readonly(this._state).error
  }

  /**
   * Returns true when undo is possible.
   * @returns {Boolean}
   */
  get canUndo() {
    return readonly(this._state).canUndo
  }

  /**
   * Returns true when redo is possible.
   * @returns {Boolean}
   */
  get canRedo() {
    return readonly(this._state).canRedo
  }

  /**
   * Sets index of focused route waypoint
   * @param {number} index
   */
  set focusedWaypointIndex(index) {
    this._state.focusedWaypointIndex = index
  }

  /**
   * Returns index of current focused route waypoint
   * @param {number}
   */
  get focusedWaypointIndex() {
    return readonly(this._state).focusedWaypointIndex
  }

  /**
   * Fetches the current route with directions. As routes are built, they do not
   * include turn-by-turn directions as those are unused in the builder. However,
   * we want to save routes with these directions for offline/cross-platform use.
   * @returns {Route} route
   */
  async fetchRouteWithDirections() {
    const current = this.route
    if (!current) {
      return current
    }

    await this._syncRoute(this._state.waypoints, { includeSteps: true })
    return this.route
  }

  /**
   * Reset the default state.
   */
  reset() {
    this._state = Object.assign(this._state, cloneDeep(resetState))

    // Cancel pending throttled functions to reset their timers.
    this._resetPreview()
  }

  /**
   * Clear the current route state.
   */
  clear() {
    this._state = Object.assign(this._state, cloneDeep(clearState))
    eventsStore.updateClearCount()
    // Cancel pending throttled functions to reset their timers.
    this._resetPreview()

    // Add a zero entry to the undo stack to represent the clear state.
    this._addToUndoStack({ route: null, waypoints: [] })
  }

  help() {
    eventsStore.updateHelpCount()
  }

  collapse() {
    eventsStore.updateCollapsedCount()
  }

  /**
   * Undo the last change to the waypoints.
   */
  undo() {
    if (!this.canUndo) {
      return
    }

    const { undoStack, undoStackIndex } = this._state
    const newIndex = undoStackIndex - 1
    eventsStore.updateUndoCount()
    this._setUndoStackIndex(newIndex)
    this._onUndoRedo(undoStack[newIndex])
  }

  /**
   * Redo the last change to the waypoints.
   */
  redo() {
    if (!this.canRedo) {
      return
    }

    const { undoStack, undoStackIndex } = this._state
    const newIndex = undoStackIndex + 1

    this._setUndoStackIndex(newIndex)
    this._onUndoRedo(undoStack[newIndex])
  }

  /**
   * Preview the effect of adding a waypoint to the route.
   * @param {Geographic} location
   */
  async previewWaypoint(location) {
    const { waypoints } = this._state
    if (waypoints.length === 0) {
      return
    }

    // De-dup terminal points by exiting early.
    if (isTerminalPointDuplicated(waypoints, location)) {
      return
    }

    // Record the preview state for the next render loop.
    const previewWaypoints = [...waypoints, location]
    await this._syncPreviewThrottled(previewWaypoints)
  }

  /**
   * Clear current preview
   */
  clearPreview() {
    this._resetPreview()
  }

  /**
   * Append a waypoint to the route.
   * @param {Geographic} location
   */
  async addWaypoint(location) {
    const { waypoints } = this._state

    // De-dup terminal points by exiting early.
    if (isTerminalPointDuplicated(waypoints, location)) {
      return
    }

    // If this is the first waypoint added, accept it. Otherwise sync waypoints
    // against a route from the API.
    if (waypoints.length === 0) {
      this._state.waypoints.push(location)
      this._addToUndoStack({ waypoints: [location] })
      return
    }

    const requestWaypoints = [...waypoints, location]
    await this._syncRoute(requestWaypoints)

    pubsub.publish('route-add-waypoint')
  }

  /**
   * Insert a waypoint along the route.
   * @param {Geographic} location
   * @returns {Number} Returns the index on the inserted waypoint.
   */
  insertWaypoint(location) {
    const { route, waypoints } = this._state
    if (!route) {
      return
    }

    // Insert the waypoint.
    const requestWaypoints = [...waypoints]
    const newWaypointIndex = route.findInsertIndex(location)
    requestWaypoints.splice(newWaypointIndex, 0, location)
    this._state.focusedWaypointIndex = null
    this._syncRoute(requestWaypoints)
    pubsub.publish('route-update-waypoint')
    return newWaypointIndex
  }

  /**
   * Update an existing waypoint.
   * @param {Number} index
   * @param {Geographic} location
   * @param {Object} options
   * @param {Boolean} options.shouldPreview
   */
  updateWaypoint(index, location, { shouldPreview = false } = {}) {
    const { syncWaypoints, waypoints } = this._state

    // Use in-flight, unsynced waypoints if available.
    const unsyncedWaypoints = []
    if (syncWaypoints?.length) {
      unsyncedWaypoints.push(...syncWaypoints)
    } else {
      unsyncedWaypoints.push(...waypoints)
    }
    unsyncedWaypoints[index] = location

    if (shouldPreview) {
      this._syncPreviewThrottled(unsyncedWaypoints)
    } else {
      this._syncRoute(unsyncedWaypoints)
    }
    pubsub.publish('route-update-waypoint')
  }

  /**
   * Update snapping based on existing waypoint snapRadius.
   * @param {Number} index
   */
  syncShouldSnap(index) {
    const { route } = this._state
    if (index) {
      const isWaypointSnapped = route?.waypoints[index]?.snapRadius > 0
      this._state.shouldSnapTemp = this._state.shouldSnap
      this.shouldSnap = isWaypointSnapped
    }
  }

  restoreShouldSnap() {
    if (this._state.shouldSnapTemp) {
      this.shouldSnap = this._state.shouldSnapTemp
      this._state.shouldSnapTemp = null
    }
  }

  /**
   * Remove a waypoint by index.
   * @param {Number} index
   */
  removeWaypoint(index) {
    const { waypoints } = this._state

    const requestWaypoints = [...waypoints]
    requestWaypoints.splice(index, 1)

    this._state.focusedWaypointIndex = null

    // If removing a waypoint leaves less than two waypoints, clear the current
    // route and update the waypoint state directly.
    if (waypoints.length <= 2) {
      this._state.route = null
      return
    }
    this._syncRoute(requestWaypoints)
    pubsub.publish('route-update-waypoint')
  }

  /**
   * Returns the snap radius to use for new waypoints.
   * @returns {Number}
   */
  get _snapRadius() {
    const { shouldSnap } = this._state
    if (shouldSnap) {
      // If we know the map zoom level, calculate a snap radius of ~200 pixels.
      const mapZoom = camera()?.location?.height
      if (mapZoom) {
        // See https://docs.mapbox.com/help/glossary/zoom-level/. Using Latitude 40.
        const level = Math.floor(mapZoom)
        const exp = 22 - level
        const baseFactor = 0.0145
        const metersPerPixel = baseFactor * Math.pow(2, exp)
        return Math.ceil(200 * metersPerPixel)
      }

      // Otherwise, use a sensible default
      return DEFAULT_SNAP_RADIUS
    }

    // Don't snap, which is marked by zero.
    return 0
  }

  /**
   * Sync fetches a route for the current waypoints.
   * @param {Array<Geographic>} waypointLocations
   * @param {Object} options
   * @param {Boolean} options.includeSteps Whether to include turn-by-turn directions from the response.
   */
  async _syncRoute(waypointLocations, options = {}) {
    // Fetch a route reflecting the current waypoints.
    const waypoints = this._getRouteRequestWaypoints(waypointLocations)
    if (waypoints.length < 2) {
      return
    }

    let route, error
    try {
      // Once a route sync is triggered, the user has committed their waypoints
      // and any previews are invalid. Clear the pending preview queue so that
      // stale requests aren't blocking the sync request.
      this._previewFetcher.abortAll()

      // Stash the waypoints pending sync. These are used when updating a waypoint
      // immediately after inserting to ensure that the point exists for reference
      // before the sync completes.
      this._state.syncWaypoints = waypoints

      route = await fetchRoute(waypoints, {
        excludeSteps: !options.includeSteps,
      })
    } catch (err) {
      error = err
    } finally {
      this._state.error = error
      if (error) {
        eventsStore.updateErrorCount()
      }

      // Release the temp sync waypoints.
      this._state.syncWaypoints = []

      if (route) {
        this._state.route = route
        this._addToUndoStack({ route })
      }
    }
  }

  /**
   * Sync fetches a preview for the current preview waypoints.
   * @param {Array<Geographic>} waypointLocations
   */
  async _syncPreview(waypointLocations) {
    const waypoints = this._getPreviewRequestWaypoints(waypointLocations)
    if (waypoints.length < 2) {
      return
    }

    // If the terminal waypoints are beyond the max allowable distance for
    // previews, do not sync the preview.
    const previewDx = turf.distance(
      waypoints[0].location,
      waypoints[waypoints.length - 1].location,
      { units: 'kilometers' }
    )
    if (previewDx > MAX_PREVIEW_DISTANCE_KM) {
      this._resetPreview()
      return
    }

    // If the preview waypoint is not to be snapped to the routing graph, return
    // immediately with a simple line. This is what the server will return, so
    // it saves a roundtrip and makes "line" editing immediate.
    let previewGeoJson
    let previewDistance
    if (!this._state.shouldSnap) {
      previewGeoJson = {
        type: 'LineString',
        coordinates: waypoints.map((wpt) => wpt.location),
      }
    }
    // Otherwise, have to talk with the server to resolve against the graph.
    else {
      try {
        const { route, aborted } = await this._previewFetcher.fetch(waypoints)

        // Skip this sync request when aborted.
        if (aborted) {
          return
        }

        previewGeoJson = route.geoJson
        previewDistance = route?.data.distance
      } catch {
        // Ignore errors from previews for now, as they can occur/expire rapidly
        // while the user is building their route. This makes for a lot of visual
        // noise. Also, failed previews cause the preview to disappear, which
        // could be intuitive enough to the user that an error message is TMI.
      }
    }

    this._state.previewSegment = {
      geoJson: previewGeoJson,
      distance: previewDistance,
    }
  }

  /**
   * Resets preview state and discards all pending server requests.
   */
  _resetPreview() {
    this._state.previewSegment = null
    this._previewFetcher.abortAll()
    this._syncPreviewThrottled.cancel()
  }

  /**
   * Returns waypoints for the route request, applying current state to any
   * waypoints not already resolved in the current route.
   * @param {Array<Geographic>} locations
   * @returns {[{location:Array<Number>, snapRadius:Number}]}
   */
  _getRouteRequestWaypoints(locations) {
    const { route } = this._state
    const snapRadius = this._snapRadius

    const waypoints = []
    for (const location of locations) {
      const routeWaypoint = route?.findWaypoint(location)
      if (routeWaypoint) {
        waypoints.push(routeWaypoint)
      } else if (location.hasOwnProperty('longitude')) {
        // location is a Geographic object
        waypoints.push({ location: location.toDegreesTuple(), snapRadius })
      } else {
        // location is already in the right format
        waypoints.push(location)
      }
    }
    return waypoints
  }

  /**
   * Returns waypoints for the preview request, choosing only those waypoints
   * required to render a preview of a change in route.
   * @param {Array<Geographic>} locations
   * @returns {[{location:Array<Number>, snapRadius:Number}]}
   */
  _getPreviewRequestWaypoints(locations) {
    const { route } = this._state
    const snapRadius = this._snapRadius

    // When editing an existing route, need to reference those waypoints.
    // Otherwise, if starting a route (and haven't commited waypoints yet), then
    // use the waypoints the user has added, mapping them to route-style waypoints.
    if (!route) {
      return locations.map((loc) => ({
        location: loc.toDegreesTuple(),
        snapRadius,
      }))
    }

    // Find the new/updated point and determine which waypoints need to be
    // included in the request. Apply the current snap radius to the point.
    const waypoints = []
    for (let i = 0, n = locations.length; i < n; i++) {
      const location = locations[i]

      // Skip ahead to first (and only) changed point.
      const unchangedWaypoint = route.findWaypoint(location)
      if (unchangedWaypoint) {
        continue
      }

      // If not updating the first waypoint, need the previous waypoint.
      if (i > 0) {
        waypoints.push(route.waypoints[i - 1])
      }

      if (location.hasOwnProperty('longitude')) {
        // location is a Geographic object
        waypoints.push({ location: location.toDegreesTuple(), snapRadius })
      } else {
        // location is already in the right format
        waypoints.push(location)
      }

      // If not updating the last waypoint, need the next waypoint.
      if (i < route.waypoints.length - 1) {
        waypoints.push(route.waypoints[i + 1])
      }

      break
    }

    return waypoints
  }

  _addToUndoStack({ route, waypoints }) {
    // Clear any redo stack and push the new route atop the undo stack.
    const { undoStack, undoStackIndex } = this._state
    const nextIndex = undoStackIndex + 1
    if (this.canRedo) {
      undoStack.splice(nextIndex)
    }
    undoStack.push({ route: route || null, waypoints: waypoints || [] })
    this._setUndoStackIndex(nextIndex)
  }

  /**
   * Updates the current index in the undo stack.
   * @param {Number} value
   */
  _setUndoStackIndex(value) {
    const min = 0
    const max = this._state.undoStack.length - 1

    this._state.undoStackIndex = value

    // Setting these here so that they are observable outside the service.
    this._state.canUndo = value > min
    this._state.canRedo = value < max
  }

  _onUndoRedo(currentValue) {
    // Clear any active previews on undo/redo.
    this._resetPreview()

    // Update state.
    this._state.error = null
    if (currentValue?.route) {
      this._state.route = currentValue.route
    } else {
      this._state.route = null
      if (currentValue?.waypoints.length) {
        this._state.waypoints = currentValue.waypoints
      } else {
        this._state.waypoints = []
      }
    }
  }
}

// Export a singleton of the service for use in the application.
const singleton = new RouteService()
export default singleton

export class Route {
  constructor(route) {
    this.data = route

    // Memoize the decoded geojson.
    const geoJson = toGeoJSON(route.geometry)
    this.geoJson = geoJson

    // Save off the route waypoints for future reference.
    const waypoints = route.legs[0].waypoints
    this.waypoints = waypoints

    // Parse waypoint locations from the route.
    this.waypointLocations = waypoints.map((wpt) =>
      Geographic.fromDegreesTuple(wpt.location)
    )

    // Create an index used to test for existence.
    this._waypointsIndex = this.waypoints.reduce(
      (index, wpt) => ({
        ...index,
        [waypointIndexKey(Geographic.fromDegreesTuple(wpt.location))]: wpt,
      }),
      {}
    )

    // Generate a turf js route so we can use some of the lib utils.
    const coordinates = this.removeConsecutiveDuplicateCoordinates(
      this.geoJson.coordinates
    )
    this._turfRoute = turf.lineString(coordinates)

    // Map the line index corresponding to each waypoint to facilitate finding
    // the insertion point for new waypoints.
    this._waypointLineIndexes = this.waypointLocations.map((wpt) =>
      coordinates.findIndex(
        (v) => 1 > turf.distance(wpt.toTurfPoint(), v, { units: 'meters' })
      )
    )
  }

  /*
   * Filter out any coordinates that are consecutive duplicates. These cause
   * problems when using Turf's nearestPointOnLine function.
   */
  removeConsecutiveDuplicateCoordinates(coords) {
    let prevCoordinate = ''
    return coords.filter((item) => {
      const isNotSameAsPrevious = item.toString() !== prevCoordinate
      prevCoordinate = item.toString()
      return isNotSameAsPrevious
    })
  }

  findInsertIndex(location) {
    // Find the index of the line segment nearest to this point.
    const nearestPointOnLine = turf.nearestPointOnLine(
      this._turfRoute,
      location.toTurfPoint(),
      { units: 'meters' }
    )

    const lineIndex = nearestPointOnLine.properties.index

    // Determine where this point should be inserted. Find the first waypoint
    // that is found later in the path, and insert the new location before it.
    for (const [i, waypointLineIndex] of this._waypointLineIndexes.entries()) {
      // If a waypoint starts on this nearest line segment, insert the new location after.
      if (waypointLineIndex === lineIndex) {
        return i + 1
      }
      if (waypointLineIndex > lineIndex) {
        return i
      }
    }
    return -1
  }

  findWaypoint(location) {
    return this._waypointsIndex[waypointIndexKey(location)]
  }

  findLineSegmentDistance(firstWaypointIndex, lastWaypointIndex) {
    const startSliceIndex = this._waypointLineIndexes[firstWaypointIndex]
    const endSliceIndex = this._waypointLineIndexes[lastWaypointIndex] + 1

    const segmentCoords = this._turfRoute.geometry.coordinates.slice(
      startSliceIndex,
      endSliceIndex
    )
    const segmentLineString = turf.lineString(segmentCoords)
    const segmentLength = turf.length(segmentLineString, {
      units: 'kilometers',
    })

    return segmentLength
  }
}

/**
 * Returns true when the last lngLat is duplicated by the given value.
 * @param {Array<Geographic>} locations
 * @param {Geographic} location
 * @returns
 */
function isTerminalPointDuplicated(locations, location) {
  if ((locations?.length || 0) > 0) {
    const last = locations[locations.length - 1]
    return last.equals(location, 6)
  }
  return false
}

/**
 * Returns a string key value for ewaypoints in the waypoint index.
 * @param {Geographic} location
 * @returns
 */
function waypointIndexKey(location) {
  return `${location.longitude}:${location.latitude}`
}

/**
 * Returns the approximate meters per pixel at the given map zoom.
 * See https://docs.mapbox.com/help/glossary/zoom-level/; we use latitude 40.
 */
export function metersPerPixel(mapZoom) {
  const level = Math.floor(mapZoom)
  const exp = 22 - level
  const baseFactor = 0.0145
  const metersPerPixel = baseFactor * Math.pow(2, exp)
  return metersPerPixel
}
